import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSENT_KEY = "dosmass_cookie_consent";
const CONSENT_VERSION = 1;

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && obj.version === CONSENT_VERSION) return obj;
    return null;
  } catch {
    return null;
  }
}

function storeConsent(prefs) {
  const obj = {
    version: CONSENT_VERSION,
    essential: true,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    date: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(obj));
  // Fire a custom event so the rest of the app can react (e.g., enable analytics)
  window.dispatchEvent(new CustomEvent("dosmass:consent", { detail: obj }));
}

export default function CookieBanner() {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [content, setContent] = useState({});
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!getStoredConsent()) {
      // Small delay so banner doesn't flash during initial paint
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/cms/content`, { params: { page: "cookies", lang } })
      .then((r) => {
        if (mounted) setContent(r.data.content || {});
      })
      .catch(() => { /* fallback to hardcoded defaults below */ });
    return () => { mounted = false; };
  }, [lang]);

  const t = (key, fallback) => content[key]?.value || fallback;

  const accept = (prefsObj) => {
    storeConsent(prefsObj);
    setVisible(false);
    setShowModal(false);
  };

  const acceptAll = () => accept({ analytics: true, marketing: true });
  const rejectAll = () => accept({ analytics: false, marketing: false });
  const savePrefs = () => accept(prefs);

  if (!visible) return null;

  return (
    <>
      <div className="cookie-banner" data-testid="cookie-banner" role="dialog" aria-live="polite">
        <div className="cookie-banner__content">
          <div className="cookie-banner__text">
            <h3 data-testid="cookie-title">{t("cookies.title", "🍪 Usamos cookies")}</h3>
            <p>
              {t(
                "cookies.description",
                "Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el uso del sitio y personalizar contenido."
              )}
              <a href="/privacy" className="cookie-banner__link">
                {t("cookies.policy_link", "Ver Política de Privacidad")}
              </a>
            </p>
          </div>
          <div className="cookie-banner__actions">
            <button
              type="button"
              className="cookie-btn cookie-btn--ghost"
              onClick={() => setShowModal(true)}
              data-testid="cookie-customize"
            >
              {t("cookies.customize", "Personalizar")}
            </button>
            <button
              type="button"
              className="cookie-btn cookie-btn--ghost"
              onClick={rejectAll}
              data-testid="cookie-reject"
            >
              {t("cookies.reject_all", "Rechazar no esenciales")}
            </button>
            <button
              type="button"
              className="cookie-btn cookie-btn--primary"
              onClick={acceptAll}
              data-testid="cookie-accept-all"
            >
              {t("cookies.accept_all", "Aceptar todas")}
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="cookie-modal-backdrop"
          onClick={() => setShowModal(false)}
          data-testid="cookie-modal"
        >
          <div
            className="cookie-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cookie-modal__head">
              <h3>{t("cookies.modal_title", "Preferencias de cookies")}</h3>
              <button
                type="button"
                className="cookie-modal__close"
                onClick={() => setShowModal(false)}
                aria-label={t("cookies.close", "Cerrar")}
              >×</button>
            </div>

            <div className="cookie-modal__body">
              <div className="cookie-row">
                <div className="cookie-row__info">
                  <strong>{t("cookies.category_essential_title", "Esenciales")}</strong>
                  <p>{t("cookies.category_essential_desc", "Necesarias para el funcionamiento del sitio.")}</p>
                </div>
                <label className="switch switch--locked" aria-disabled="true">
                  <input type="checkbox" checked readOnly disabled />
                  <span />
                </label>
              </div>

              <div className="cookie-row">
                <div className="cookie-row__info">
                  <strong>{t("cookies.category_analytics_title", "Analíticas")}</strong>
                  <p>{t("cookies.category_analytics_desc", "Nos ayudan a entender cómo los visitantes interactúan con el sitio.")}</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    data-testid="cookie-toggle-analytics"
                  />
                  <span />
                </label>
              </div>

              <div className="cookie-row">
                <div className="cookie-row__info">
                  <strong>{t("cookies.category_marketing_title", "Marketing")}</strong>
                  <p>{t("cookies.category_marketing_desc", "Permiten mostrar contenido y anuncios relevantes según tus intereses.")}</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    data-testid="cookie-toggle-marketing"
                  />
                  <span />
                </label>
              </div>
            </div>

            <div className="cookie-modal__foot">
              <button
                type="button"
                className="cookie-btn cookie-btn--ghost"
                onClick={rejectAll}
              >
                {t("cookies.reject_all", "Rechazar no esenciales")}
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn--primary"
                onClick={savePrefs}
                data-testid="cookie-save-prefs"
              >
                {t("cookies.save_preferences", "Guardar preferencias")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
